import { useCallback, useEffect, useMemo, useState } from 'react'
import { Header } from '@/components/layout/Header'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Loader2,
  Search,
  TrendingDown,
  TrendingUp,
  Zap,
  History,
  FileText,
  Globe,
  ArrowUpRight,
  ArrowDownRight,
  ArrowRight,
  Clock,
  Users,
  Bell,
  BookOpen,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useCoinMarketData } from '@/hooks/useCoinMarketData'
import { useAuth } from '@/hooks/useAuth'
import { useExchangeOptions } from '@/hooks/useExchangeOptions'
import { useNamedPresets } from '@/hooks/useNamedPresets'
import { ExchangeBadge } from '@/components/common/ExchangeBadge'
import { HistoryNotes } from '@/components/common/HistoryNotes'
import { OpenInScreenerButton } from '@/components/common/OpenInScreenerButton'
import { PresetControls } from '@/components/common/PresetControls'
import { ResearchContent } from '@/components/common/ResearchContent'
import { CoinTradeSummary } from '@/components/coin/CoinTradeSummary'
import { CoinLiveTrades } from '@/components/coin/CoinLiveTrades'
import { CoinTags } from '@/components/coin/CoinTags'
import { useLivePositions } from '@/hooks/usePositions'
import { getCoinAnalyzeStatus, getCoinHistory, getCoinInfo, getCoinTrades, triggerCoinAnalyze } from '@/lib/api'
import type {
  CoinMarketData,
  MarketRecord,
  PositionHistoryData,
  TickerInfo,
  AnalyzeResult,
  AnalyzeTradeFlow,
} from '@/types'
import { FALLBACK_EXCHANGES } from '@/types'

const PERIODS = [
  { value: '1m', label: '1 Minute' },
  { value: '5m', label: '5 Minutes' },
  { value: '15m', label: '15 Minutes' },
  { value: '1h', label: '1 Hour' },
] as const

const LEGACY_LOCAL_STORAGE_KEY = 'coinOverviewSettings'

function getCoinOverviewStorageKey(username?: string | null): string {
  const normalized = (username || '').trim().toLowerCase()
  return `coinOverviewSettings:${normalized || 'anonymous'}`
}

interface Settings {
  symbols: string[]
  period: (typeof PERIODS)[number]['value']
  includeSpot: boolean
  includeFutures: boolean
  exchanges: string[]
}

function normalizeSettings(value: unknown): Settings | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const settings = value as Partial<Settings>
  if (
    !Array.isArray(settings.symbols) || !settings.symbols.every((symbol) => typeof symbol === 'string') ||
    !Array.isArray(settings.exchanges) || !settings.exchanges.every((exchange) => typeof exchange === 'string') ||
    !PERIODS.some(({ value: period }) => period === settings.period) ||
    typeof settings.includeSpot !== 'boolean' || typeof settings.includeFutures !== 'boolean'
  ) return null

  return {
    symbols: [...settings.symbols],
    period: settings.period as Settings['period'],
    includeSpot: settings.includeSpot,
    includeFutures: settings.includeFutures,
    exchanges: [...settings.exchanges],
  }
}

export default function CoinOverview() {
  const { user } = useAuth()
  const exchangeOptions = useExchangeOptions()
  const storageKey = getCoinOverviewStorageKey(user?.username)
  const [symbolInput, setSymbolInput] = useState('')
  const [symbols, setSymbols] = useState<string[]>([])
  const [period, setPeriod] = useState<Settings['period']>('1m')
  const [includeSpot, setIncludeSpot] = useState(true)
  const [includeFutures, setIncludeFutures] = useState(true)
  const [selectedExchanges, setSelectedExchanges] = useState<string[]>(FALLBACK_EXCHANGES)
  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null)
  const [listSearch, setListSearch] = useState('')
  const [loadedStorageKey, setLoadedStorageKey] = useState<string | null>(null)

  useEffect(() => {
    if (loadedStorageKey === storageKey) return

    let settings: Partial<Settings> = {}
    try {
      let saved = localStorage.getItem(storageKey)
      if (!saved) {
        const legacySaved = localStorage.getItem(LEGACY_LOCAL_STORAGE_KEY)
        if (legacySaved) {
          localStorage.setItem(storageKey, legacySaved)
          saved = legacySaved
        }
      }
      if (saved) {
        const parsed: unknown = JSON.parse(saved)
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          settings = parsed as Partial<Settings>
        }
      }
    } catch (e) {
      console.error('Failed to load settings:', e)
    }
    const savedSymbols = Array.isArray(settings.symbols)
      ? settings.symbols.filter((symbol) => typeof symbol === 'string')
      : []
    const savedExchanges = Array.isArray(settings.exchanges)
      ? settings.exchanges.filter((exchange) => typeof exchange === 'string')
      : exchangeOptions
    setSymbols(savedSymbols)
    setSymbolInput(savedSymbols.join(', '))
    setPeriod(PERIODS.find(({ value }) => value === settings.period)?.value ?? '1m')
    setIncludeSpot(typeof settings.includeSpot === 'boolean' ? settings.includeSpot : true)
    setIncludeFutures(typeof settings.includeFutures === 'boolean' ? settings.includeFutures : true)
    setSelectedExchanges(savedExchanges)
    setLoadedStorageKey(storageKey)
  }, [storageKey, exchangeOptions, loadedStorageKey])

  const saveSettings = (newSettings: Partial<Settings>) => {
    const settings: Settings = {
      symbols,
      period,
      includeSpot,
      includeFutures,
      exchanges: selectedExchanges,
      ...newSettings,
    }
    try {
      localStorage.setItem(storageKey, JSON.stringify(settings))
    } catch (e) {
      console.error('Failed to save settings:', e)
    }
  }

  const filterPresets = useNamedPresets<Settings>({
    user,
    preferenceKey: 'coin_overview_presets',
    value: { symbols, period, includeSpot, includeFutures, exchanges: selectedExchanges },
    normalize: normalizeSettings,
    disabled: loadedStorageKey !== storageKey,
    onApply: (settings) => {
      if (loadedStorageKey !== storageKey) return false
      setSymbols(settings.symbols)
      setSymbolInput(settings.symbols.join(', '))
      setPeriod(settings.period)
      setIncludeSpot(settings.includeSpot)
      setIncludeFutures(settings.includeFutures)
      setSelectedExchanges(settings.exchanges)
      saveSettings(settings)
    },
  })

  const { data, isLoading, error, lastUpdated } = useCoinMarketData({
    symbols,
    period,
    includeSpot,
    includeFutures,
    exchanges: selectedExchanges,
    // Restoring saved filters must finish before a request for all markets starts.
    enabled: loadedStorageKey === storageKey,
  })

  const selectedCoin = useMemo(
    () => data.find((coin) => coin.symbol === selectedSymbol) ?? null,
    [data, selectedSymbol]
  )

  useEffect(() => {
    if (data.length === 0) {
      setSelectedSymbol(null)
      return
    }
    if (!selectedSymbol || !data.some((coin) => coin.symbol === selectedSymbol)) {
      setSelectedSymbol(data[0].symbol)
    }
  }, [data, selectedSymbol])

  const filteredCoins = useMemo(() => {
    const query = listSearch.trim().toUpperCase()
    if (!query) return data

    return data.filter((coin) => {
      const source = `${coin.baseSymbol} ${coin.symbol}`.toUpperCase()
      return source.includes(query)
    })
  }, [data, listSearch])

  const symbolSuggestions = useMemo(() => {
    const merged = new Set<string>()
    for (const symbol of symbols) merged.add(symbol.toUpperCase())
    for (const coin of data) {
      merged.add(coin.baseSymbol.toUpperCase())
      merged.add(coin.symbol.toUpperCase())
    }

    const token = symbolInput.split(',').at(-1)?.trim().toUpperCase() || ''
    const all = Array.from(merged).sort()
    if (!token) return all.slice(0, 16)
    return all.filter((s) => s.includes(token)).slice(0, 16)
  }, [data, symbolInput, symbols])

  const handleApplySymbols = () => {
    const newSymbols = symbolInput
      .split(',')
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean)

    setSymbols(newSymbols)
    saveSettings({ symbols: newSymbols })
  }

  const handleClearSymbols = () => {
    setSymbols([])
    setSymbolInput('')
    saveSettings({ symbols: [] })
  }

  const addSuggestedSymbol = (symbol: string) => {
    const merged = Array.from(new Set([...symbols.map((s) => s.toUpperCase()), symbol.toUpperCase()]))
    setSymbols(merged)
    setSymbolInput(merged.join(', '))
    saveSettings({ symbols: merged })
  }

  const handlePeriodChange = (newPeriod: string) => {
    const nextPeriod = PERIODS.find(({ value }) => value === newPeriod)?.value
    if (!nextPeriod) return
    setPeriod(nextPeriod)
    saveSettings({ period: nextPeriod })
  }

  const handleSpotToggle = (checked: boolean) => {
    setIncludeSpot(checked)
    saveSettings({ includeSpot: checked })
  }

  const handleFuturesToggle = (checked: boolean) => {
    setIncludeFutures(checked)
    saveSettings({ includeFutures: checked })
  }

  const toggleExchange = (exchange: string) => {
    const newExchanges = selectedExchanges.includes(exchange)
      ? selectedExchanges.filter((e) => e !== exchange)
      : [...selectedExchanges, exchange]

    setSelectedExchanges(newExchanges)
    saveSettings({ exchanges: newExchanges })
  }

  const selectAllExchanges = () => {
    setSelectedExchanges(exchangeOptions)
    saveSettings({ exchanges: exchangeOptions })
  }

  const deselectAllExchanges = () => {
    setSelectedExchanges([])
    saveSettings({ exchanges: [] })
  }

  return (
    <div className="flex flex-col">
      <Header title="Coin" />

      <div className="flex-1 space-y-6 p-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <PresetControls label="Coin Overview filters" presets={filterPresets} />
            <div className="space-y-2">
              <Label htmlFor="symbols">Symbols (comma-separated)</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="symbols"
                    placeholder="BTC, ETH, SOL, ..."
                    value={symbolInput}
                    onChange={(e) => setSymbolInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleApplySymbols()
                      }
                    }}
                    className="pl-9"
                  />
                </div>
                <Button onClick={handleApplySymbols}>Apply</Button>
                {symbols.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={handleClearSymbols}>
                    Clear
                  </Button>
                )}
              </div>

              {symbolSuggestions.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-muted-foreground">Symbol matches:</span>
                  {symbolSuggestions.map((symbol) => (
                    <Badge
                      key={symbol}
                      variant={symbols.includes(symbol) ? 'default' : 'secondary'}
                      className="cursor-pointer"
                      onClick={() => addSuggestedSymbol(symbol)}
                    >
                      {symbol}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-4 items-end">
              <div className="space-y-2">
                <Label htmlFor="period">Period</Label>
                <Select value={period} onValueChange={handlePeriodChange}>
                  <SelectTrigger id="period" className="w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PERIODS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="spot"
                  checked={includeSpot}
                  onCheckedChange={handleSpotToggle}
                />
                <Label htmlFor="spot" className="cursor-pointer">
                  Spot
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="futures"
                  checked={includeFutures}
                  onCheckedChange={handleFuturesToggle}
                />
                <Label htmlFor="futures" className="cursor-pointer">
                  Futures
                </Label>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Exchanges</Label>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={selectAllExchanges}>
                    Select All
                  </Button>
                  <Button variant="ghost" size="sm" onClick={deselectAllExchanges}>
                    Clear
                  </Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {exchangeOptions.map((exchange) => (
                  <Badge
                    key={exchange}
                    variant={selectedExchanges.includes(exchange) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => toggleExchange(exchange)}
                  >
                    {exchange.charAt(0).toUpperCase() + exchange.slice(1)}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {lastUpdated && (
          <div className="text-sm text-muted-foreground">
            Last updated: {lastUpdated.toLocaleTimeString()}
            {symbols.length > 0 && (
              <span className="ml-2">· Filtered: {symbols.join(', ')}</span>
            )}
          </div>
        )}

        {error && (
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-5 w-5" />
                <span>{error}</span>
              </div>
            </CardContent>
          </Card>
        )}


        {isLoading && data.length === 0 && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        )}

        {data.length > 0 && (
          <div className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <Card className="overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <CardTitle className="text-base">Tickers</CardTitle>
                  <div className="relative w-full md:w-[280px]">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={listSearch}
                      onChange={(e) => setListSearch(e.target.value)}
                      placeholder="Search loaded symbols..."
                      className="pl-9"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-14">#</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead className="text-right">Volume (24h)</TableHead>
                      <TableHead className="text-right">Open Interest</TableHead>
                      <TableHead className="text-right">Markets</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCoins.map((coin, index) => (
                      <TableRow
                        key={coin.symbol}
                        className="cursor-pointer"
                        data-state={coin.symbol === selectedSymbol ? 'selected' : undefined}
                        onClick={() => setSelectedSymbol(coin.symbol)}
                      >
                        <TableCell className="text-muted-foreground">{index + 1}</TableCell>
                        <TableCell>
                          <div className="font-semibold">{coin.baseSymbol}</div>
                          <div className="text-xs text-muted-foreground">{coin.symbol}</div>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatUsd(coin.avgPrice)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div>{formatVolumeUsd(coin.totalVolumeCurrent)}</div>
                          <div
                            className={cn(
                              'text-xs font-medium',
                              coin.volumeChangePercent == null
                                ? 'text-muted-foreground'
                                : coin.volumeChangePercent >= 0 ? 'text-green-500' : 'text-red-500'
                            )}
                          >
                            {formatSignedPercent(coin.volumeChangePercent)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {coin.futuresCount > 0 ? (
                            <>
                              <div>{formatCompactNumber(coin.totalOICurrent)}</div>
                              <div
                                className={cn(
                                  'text-xs font-medium',
                                  coin.oiChangePercent >= 0 ? 'text-green-500' : 'text-red-500'
                                )}
                              >
                                {formatSignedPercent(coin.oiChangePercent)}
                              </div>
                            </>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex gap-1">
                            {coin.spotCount > 0 && (
                              <Badge variant="secondary" className="text-xs">
                                {coin.spotCount} Spot
                              </Badge>
                            )}
                            {coin.futuresCount > 0 && (
                              <Badge variant="secondary" className="text-xs">
                                {coin.futuresCount} Fut
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredCoins.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                          No symbols match your search
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card className="overflow-hidden">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Ticker Details</CardTitle>
              </CardHeader>
              <CardContent>
                {selectedCoin ? (
                  <CoinDetail coin={selectedCoin} />
                ) : (
                  <div className="py-8 text-center text-muted-foreground">
                    Select a ticker from the list to view details
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {!isLoading && data.length === 0 && !error && (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center text-muted-foreground py-8">
                {symbols.length > 0
                  ? `No market data available for: ${symbols.join(', ')}. Try clearing the filter.`
                  : 'No market data available. Ensure exchange connections are active.'}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}

interface CoinDetailProps {
  coin: CoinMarketData
}

function CoinDetail({ coin }: CoinDetailProps) {
  const [historyData, setHistoryData] = useState<PositionHistoryData | null>(null)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [tickerInfo, setTickerInfo] = useState<TickerInfo | null>(null)
  const [infoLoading, setInfoLoading] = useState(false)
  const [tradeFlow, setTradeFlow] = useState<AnalyzeTradeFlow | null>(null)
  const [tradeFlowLoading, setTradeFlowLoading] = useState(false)
  const [tradeFlowError, setTradeFlowError] = useState<string | null>(null)
  const [analyzeResult, setAnalyzeResult] = useState<AnalyzeResult | null>(null)
  const [analyzeLoading, setAnalyzeLoading] = useState(false)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }))
  }

  // Fetch history and info when coin changes  
  useEffect(() => {
    setHistoryData(null)
    setTickerInfo(null)
    setTradeFlow(null)
    setTradeFlowError(null)
    setAnalyzeResult(null)

    const ticker = coin.baseSymbol

    // Fetch position history
    setHistoryLoading(true)
    getCoinHistory(ticker)
      .then((data) => setHistoryData(data))
      .catch(() => setHistoryData(null))
      .finally(() => setHistoryLoading(false))

    // Fetch ticker info
    setInfoLoading(true)
    getCoinInfo(ticker)
      .then((data) => setTickerInfo(data))
      .catch(() => setTickerInfo(null))
      .finally(() => setInfoLoading(false))

    // Trade diagnostics are useful without running the expensive AI analyze flow.
    setTradeFlowLoading(true)
    getCoinTrades(ticker, 60)
      .then((flow) => setTradeFlow(flow))
      .catch((error) => {
        setTradeFlow(null)
        setTradeFlowError(error instanceof Error ? error.message : 'Unable to load trade flow')
      })
      .finally(() => setTradeFlowLoading(false))
  }, [coin.baseSymbol])

  const handleAnalyze = useCallback(async () => {
    setAnalyzeLoading(true)
    setAnalyzeResult(null)
    try {
      const result = await triggerCoinAnalyze(coin.baseSymbol)
      setAnalyzeResult(result)
      if (result.sections.trade_flow) setTradeFlow(result.sections.trade_flow)
      setExpandedSections((prev) => ({ ...prev, analyze: true }))
    } catch (err) {
      console.error('Analyze failed:', err)
    } finally {
      setAnalyzeLoading(false)
    }
  }, [coin.baseSymbol])

  useEffect(() => {
    const deepStatus = analyzeResult?.deep_status
    if (!deepStatus || (deepStatus.status !== 'pending' && deepStatus.status !== 'running')) {
      return
    }

    let cancelled = false
    let attempts = 0
    let timeoutId: number | undefined
    const jobId = deepStatus.job_id

    const poll = async () => {
      if (cancelled) {
        return
      }
      attempts += 1
      try {
        const status = await getCoinAnalyzeStatus(coin.baseSymbol)
        if (cancelled) {
          return
        }
        if (jobId && status.job_id && status.job_id !== jobId) {
          return
        }
        if (status.sections.trade_flow) setTradeFlow(status.sections.trade_flow)
        setAnalyzeResult((prev) => {
          if (!prev) {
            return prev
          }
          if (jobId && status.job_id && prev.deep_status?.job_id && status.job_id !== prev.deep_status.job_id) {
            return prev
          }
          return {
            ...prev,
            sections: {
              ...prev.sections,
              ...status.sections,
            },
            deep_status: {
              status: status.status,
              job_id: status.job_id,
              cached_at: status.cached_at,
              updated_at: status.updated_at,
              error: status.error,
              section_status: status.section_status,
              section_errors: status.section_errors,
            },
          }
        })
        if (status.status === 'complete' || status.status === 'error' || status.status === 'idle') {
          return
        }
      } catch (err) {
        console.error('Analyze status polling failed:', err)
      }
      if (!cancelled && attempts < 90) {
        timeoutId = window.setTimeout(poll, 2000)
      }
    }

    timeoutId = window.setTimeout(poll, 1000)
    return () => {
      cancelled = true
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [analyzeResult?.deep_status?.job_id, analyzeResult?.deep_status?.status, coin.baseSymbol])

  return (
    <div className="space-y-4">
      {/* Coin actions */}
      <div className="space-y-1">
        <div className="flex items-end justify-between">
          <div>
            <h3 className="text-3xl font-bold tracking-tight">{coin.baseSymbol}</h3>
            <p className="text-sm text-muted-foreground">{coin.symbol}</p>
          </div>
          <div className="text-right space-y-2">
            <div className="text-2xl font-semibold">{formatUsd(coin.avgPrice)}</div>
            <div className="flex flex-wrap items-center gap-2 justify-end">
              <Badge variant="outline">{coin.exchangeCount} exchanges</Badge>
              <OpenInScreenerButton symbol={coin.baseSymbol} />
              <Button
                size="sm"
                onClick={handleAnalyze}
                disabled={analyzeLoading}
                className="gap-1.5"
              >
                {analyzeLoading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Zap className="h-3.5 w-3.5" />
                )}
                Analyze
              </Button>
            </div>
          </div>
        </div>
      </div>

      <CoinTags key={coin.baseSymbol} symbol={coin.baseSymbol} />

      {/* Open Position Status */}
      <OpenPositionBanner symbol={coin.baseSymbol} />

      {/* Metrics */}
      <div className="grid grid-cols-2 gap-3">
        <MetricTile label="Volume (24h)" value={formatVolumeUsd(coin.totalVolumeCurrent)} />
        <MetricTile
          label="Volume Change (24h)"
          value={formatSignedPercent(coin.volumeChangePercent)}
          valueClassName={coin.volumeChangePercent == null
            ? 'text-muted-foreground'
            : coin.volumeChangePercent >= 0 ? 'text-green-500' : 'text-red-500'}
        />
        <MetricTile label="Open Interest (Current)" value={formatCompactNumber(coin.totalOICurrent)} />
        <MetricTile
          label="OI Change"
          value={formatSignedPercent(coin.oiChangePercent)}
          valueClassName={coin.oiChangePercent >= 0 ? 'text-green-500' : 'text-red-500'}
        />
        <MetricTile label="Spot Markets" value={String(coin.spotCount)} />
        <MetricTile label="Futures Markets" value={String(coin.futuresCount)} />
      </div>

      <CollapsibleSection
        title="Trades"
        icon={<Zap className="h-4 w-4" />}
        expanded={expandedSections.trades ?? true}
        onToggle={() => toggleSection('trades')}
        badge={tradeFlow ? `${tradeFlow.window_minutes}m` : undefined}
        loading={tradeFlowLoading}
      >
        <CoinLiveTrades key={coin.baseSymbol} ticker={coin.baseSymbol} enabled={expandedSections.trades ?? true} />
        {tradeFlow ? (
          <CoinTradeSummary key={tradeFlow.ticker} flow={tradeFlow} />
        ) : tradeFlowError ? (
          <p className="py-2 text-xs text-red-500">{tradeFlowError}</p>
        ) : (
          <p className="py-2 text-xs text-muted-foreground">Loading trades…</p>
        )}
      </CollapsibleSection>

      {/* Exchange Details */}
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-muted-foreground">Exchange Details</h4>
        <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
          {coin.exchanges.map((exchangeData) => (
            <ExchangeDetail key={exchangeData.exchange} exchangeData={exchangeData} />
          ))}
        </div>
      </div>

      {/* Position History Section */}
      <CollapsibleSection
        title="Position History"
        icon={<History className="h-4 w-4" />}
        expanded={expandedSections.history ?? false}
        onToggle={() => toggleSection('history')}
        badge={
          historyData
            ? `${historyData.positions.length} pos · ${historyData.notes.length} notes`
            : undefined
        }
        loading={historyLoading}
      >
        {historyData &&
        (historyData.positions.length > 0 || historyData.notes.length > 0) ? (
          <PositionHistorySection data={historyData} />
        ) : (
          <p className="text-sm text-muted-foreground py-2">No position history found.</p>
        )}
      </CollapsibleSection>

      {/* AI Research (Info) Section */}
      <CollapsibleSection
        title="AI Research"
        icon={<BookOpen className="h-4 w-4" />}
        expanded={expandedSections.info ?? false}
        onToggle={() => toggleSection('info')}
        badge={tickerInfo ? 'Available' : undefined}
        loading={infoLoading}
      >
        {tickerInfo && tickerInfo.info ? (
          <TickerInfoSection info={tickerInfo} />
        ) : (
          <p className="text-sm text-muted-foreground py-2">
            No AI research available. Use <code>/info {coin.baseSymbol}</code> in Telegram to generate.
          </p>
        )}
      </CollapsibleSection>

      {/* Analyze Results Section */}
      {analyzeResult && (
        <CollapsibleSection
          title="Analysis Results"
          icon={<Zap className="h-4 w-4" />}
          expanded={expandedSections.analyze ?? true}
          onToggle={() => toggleSection('analyze')}
          badge={`${Object.keys(analyzeResult.sections).length} sections`}
        >
          <AnalyzeResultsSection result={analyzeResult} />
        </CollapsibleSection>
      )}
    </div>
  )
}

// ============================================================================
// Collapsible Section Wrapper
// ============================================================================

interface CollapsibleSectionProps {
  title: string
  icon?: React.ReactNode
  expanded: boolean
  onToggle: () => void
  badge?: string
  loading?: boolean
  children: React.ReactNode
}

function CollapsibleSection({
  title,
  icon,
  expanded,
  onToggle,
  badge,
  loading,
  children,
}: CollapsibleSectionProps) {
  return (
    <Collapsible open={expanded} onOpenChange={onToggle}>
      <div className="rounded-md border bg-muted/10">
        <CollapsibleTrigger asChild>
          <button className="flex w-full items-center justify-between px-3 py-2.5 text-left hover:bg-muted/20 transition-colors rounded-md">
            <div className="flex items-center gap-2">
              {icon}
              <span className="text-sm font-semibold">{title}</span>
              {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
              {badge && !loading && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                  {badge}
                </Badge>
              )}
            </div>
            {expanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="px-3 pb-3">
          {children}
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}

// ============================================================================
// Position History Section
// ============================================================================

function PositionHistorySection({ data }: { data: PositionHistoryData }) {
  return (
    <div className="space-y-3">
      {data.positions.map((pos, idx) => {
        const isOpen = pos.status === 'open'
        const dirEmoji =
          pos.direction === 'long' ? '📈' : pos.direction === 'short' ? '📉' : ''
        const resultBadge = isOpen ? (
          <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-[10px]">
            OPEN
          </Badge>
        ) : pos.closed_result === 'profit' ? (
          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[10px]">
            💰 PROFIT
          </Badge>
        ) : (
          <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-[10px]">
            ❌ LOSS
          </Badge>
        )

        return (
          <div
            key={pos.id}
            className="rounded-md border bg-background/50 p-3 space-y-1.5"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">#{idx + 1}</span>
                {resultBadge}
                {dirEmoji && <span className="text-sm">{dirEmoji}</span>}
                {pos.direction && (
                  <span className="text-xs font-medium uppercase text-muted-foreground">
                    {pos.direction}
                  </span>
                )}
              </div>
              <span className="text-xs text-muted-foreground">
                {pos.created_at?.slice(0, 10)}
                {pos.closed_at ? ` → ${pos.closed_at.slice(0, 10)}` : ''}
              </span>
            </div>
            {pos.decision_reason && (
              <p className="text-xs text-muted-foreground line-clamp-2">
                💡 {pos.decision_reason}
              </p>
            )}
            {pos.closed_reason && (
              <p className="text-xs text-muted-foreground line-clamp-2">
                📝 {pos.closed_reason}
              </p>
            )}
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {pos.created_by}
              </span>
              {pos.users_in_position.length > 1 && (
                <span>+{pos.users_in_position.length - 1} users</span>
              )}
              {pos.alert_counts.length > 0 && (
                <span className="flex items-center gap-1">
                  <Bell className="h-3 w-3" />
                  {pos.alert_counts.reduce((sum, a) => sum + a.count, 0)} alerts
                </span>
              )}
            </div>
          </div>
        )
      })}

      <HistoryNotes notes={data.notes} ticker={data.ticker} />
    </div>
  )
}

// ============================================================================
// Ticker Info (AI Research) Section
// ============================================================================

function TickerInfoSection({ info }: { info: TickerInfo }) {
  const researchedDate = info.researched_at
    ? new Date(info.researched_at * 1000).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null

  return (
    <div className="space-y-4">
      {researchedDate && (
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Clock className="h-3 w-3" />
          Researched: {researchedDate}
        </div>
      )}
      {info.contract_addresses && (
        <div className="rounded-md border bg-muted/20 p-3">
          <h4 className="mb-2 text-xs font-semibold text-muted-foreground">Contract addresses</h4>
          <ResearchContent content={info.contract_addresses} />
        </div>
      )}
      <ResearchContent content={info.info || ''} />
    </div>
  )
}

// ============================================================================
// Analyze Results Section
// ============================================================================

function AnalyzeResultsSection({ result }: { result: AnalyzeResult }) {
  const { sections } = result
  const ts = new Date(result.timestamp * 1000).toLocaleString()
  const aiGeneratedAt = sections.ai_analysis?.generated_at
    ? new Date(sections.ai_analysis.generated_at * 1000).toLocaleString()
    : null

  return (
    <div className="space-y-3">
      <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
        <Clock className="h-3 w-3" />
        {ts}
      </div>
      {result.deep_status?.section_status && (
        <AnalyzeStatusStrip status={result.deep_status} />
      )}

      {/* Market Snapshot */}
      {sections.market_snapshot && (
        <AnalyzeCard title="Market Snapshot" icon="📊">
          <div className="space-y-1.5">
            {sections.market_snapshot.futures.length > 0 && (
              <div>
                <div className="text-[10px] font-semibold text-orange-400 mb-1">FUTURES</div>
                {sections.market_snapshot.futures.map((f) => (
                  <div key={f.exchange} className="flex justify-between text-xs">
                    <span className="font-medium">{f.exchange}</span>
                    <span className="space-x-2">
                      <span>{formatUsd(f.price)}</span>
                      <span className="text-muted-foreground">24h vol: {formatVolumeUsd(f.volume_24h)}</span>
                      {f.funding_rate != null && (
                        <span
                          className={cn(
                            'text-[10px]',
                            f.funding_rate >= 0 ? 'text-green-500' : 'text-red-500'
                          )}
                        >
                          FR: {(f.funding_rate * 100).toFixed(4)}%
                        </span>
                      )}
                      {f.change_pct != null && (
                        <span
                          className={cn(
                            'text-[10px]',
                            f.change_pct >= 0 ? 'text-green-500' : 'text-red-500'
                          )}
                        >
                          {f.change_pct >= 0 ? '+' : ''}
                          {f.change_pct.toFixed(2)}%
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {sections.market_snapshot.spot.length > 0 && (
              <div>
                <div className="text-[10px] font-semibold text-blue-400 mb-1">SPOT</div>
                {sections.market_snapshot.spot.map((s) => (
                  <div key={s.exchange} className="flex justify-between text-xs">
                    <span className="font-medium">{s.exchange}</span>
                    <span className="space-x-2">
                      <span>{formatUsd(s.price)}</span>
                      <span className="text-muted-foreground">24h vol: {formatVolumeUsd(s.volume_24h)}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </AnalyzeCard>
      )}

      {/* Open Interest */}
      {sections.open_interest && (
        <AnalyzeCard title="Open Interest" icon="📈">
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground mb-1">
              Total: <span className="font-semibold text-foreground">${formatCompactNumber(sections.open_interest.total_oi_usd)}</span>
              {' · '}
              <span className="font-semibold text-foreground">{formatCompactNumber(sections.open_interest.total_oi_coins)} coins</span>
            </div>
            {sections.open_interest.exchanges.map((e) => (
              <div key={e.exchange} className="flex justify-between text-xs">
                <span className="font-medium">{e.exchange}</span>
                <span>
                  ${formatCompactNumber(e.oi_usd)} · {formatCompactNumber(e.oi_coins)} coins
                </span>
              </div>
            ))}
          </div>
        </AnalyzeCard>
      )}

      {/* Orders */}
      {sections.orders && (
        <AnalyzeCard title="Active Orders" icon="📋">
          <div className="space-y-1">
            <div className="flex gap-3 text-xs mb-1.5">
              <span className="text-green-500">
                🟢 Bids: ${formatCompactNumber(sections.orders.bid_total)}
              </span>
              <span className="text-red-500">
                🔴 Asks: ${formatCompactNumber(sections.orders.ask_total)}
              </span>
            </div>
            {sections.orders.orders.slice(0, 8).map((o, i) => (
              <div key={i} className="flex justify-between text-xs">
                <span className="flex items-center gap-1.5">
                  {o.side === 'bid' ? (
                    <ArrowUpRight className="h-3 w-3 text-green-500" />
                  ) : (
                    <ArrowDownRight className="h-3 w-3 text-red-500" />
                  )}
                  <span className="font-medium">{o.exchange}</span>
                  <span className={o.side === 'bid' ? 'text-green-500' : 'text-red-500'}>
                    {o.side.toUpperCase()}
                  </span>
                </span>
                <span>
                  {formatUsd(o.price)} · ${formatCompactNumber(o.usdt_size)}
                </span>
              </div>
            ))}
            {sections.orders.orders.length > 8 && (
              <div className="text-[10px] text-muted-foreground">
                +{sections.orders.orders.length - 8} more orders
              </div>
            )}
          </div>
        </AnalyzeCard>
      )}

      {/* DEX Activity */}
      {sections.dex_activity && (
        <AnalyzeCard title="DEX Activity" icon="🌐">
          <div className="space-y-1.5">
            <div className="text-xs text-muted-foreground">
              {sections.dex_activity.tokens_found} token(s) found
              {sections.dex_activity.primary_chain && (
                <>
                  {' · Primary: '}
                  <span className="font-semibold text-foreground">
                    {sections.dex_activity.primary_chain}
                  </span>
                  {' ($'}
                  {formatCompactNumber(sections.dex_activity.primary_liquidity)}
                  {' liq)'}
                </>
              )}
            </div>
            {sections.dex_activity.tokens.map((t, i) => (
              <div key={i} className="flex items-center gap-1.5 text-xs">
                {t.is_primary && <span className="text-green-500">✅</span>}
                <span className="font-medium">{t.chain}:</span>
                <code className="text-[10px] text-muted-foreground truncate max-w-[200px]">
                  {t.address}
                </code>
                <span className="text-muted-foreground">(${formatCompactNumber(t.liquidity)})</span>
              </div>
            ))}
            {sections.dex_activity.pools.length > 0 && (
              <div className="pt-1">
                <div className="text-[10px] font-semibold text-muted-foreground mb-1">POOLS</div>
                {sections.dex_activity.pools.map((p, i) => (
                  <div key={i} className="flex justify-between text-xs">
                    <span>
                      {p.dex} {p.pair}
                    </span>
                    <span className="text-muted-foreground">
                      {p.volume_usd != null && `vol $${formatCompactNumber(p.volume_usd)}`}
                      {p.liquidity_usd != null && ` · liq $${formatCompactNumber(p.liquidity_usd)}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </AnalyzeCard>
      )}

      {/* Recent Alerts */}
      {sections.recent_alerts && (
        <AnalyzeCard title={`Alerts (24h) — ${sections.recent_alerts.total} total`} icon="🔔">
          <div className="space-y-1">
            {Object.entries(sections.recent_alerts.alert_types).map(([type, count]) => (
              <div key={type} className="flex justify-between text-xs">
                <span>{type}</span>
                <span className="font-semibold">{count}</span>
              </div>
            ))}
            {sections.recent_alerts.orderbook_summary.map((ob, i) => (
              <div key={i} className="flex justify-between text-xs">
                <span className="flex items-center gap-1">
                  {ob.side === 'bid' ? (
                    <span className="text-green-500">🟢</span>
                  ) : (
                    <span className="text-red-500">🔴</span>
                  )}
                  {ob.exchange} {ob.side.toUpperCase()}
                </span>
                <span>
                  {ob.count} alerts · ${formatCompactNumber(ob.total_size)}
                </span>
              </div>
            ))}
          </div>
        </AnalyzeCard>
      )}

      {/* AI Analysis */}
      {sections.ai_analysis?.text && (
        <AnalyzeCard title="AI Analysis" icon="🤖">
          <div className="space-y-2">
            {(aiGeneratedAt || sections.ai_analysis.model) && (
              <div className="text-[10px] text-muted-foreground">
                {aiGeneratedAt && <span>{aiGeneratedAt}</span>}
                {aiGeneratedAt && sections.ai_analysis.model && <span>{' · '}</span>}
                {sections.ai_analysis.model && <span>{sections.ai_analysis.model}</span>}
              </div>
            )}
            <ResearchContent content={sections.ai_analysis.text} />
          </div>
        </AnalyzeCard>
      )}

      {/* Ticker Info from Analyze */}
      {sections.ticker_info?.info && (
        <AnalyzeCard title="AI Research" icon="🔬">
          <TickerInfoSection info={sections.ticker_info} />
        </AnalyzeCard>
      )}

      {Object.keys(sections).length === 0 && (
        <p className="text-sm text-muted-foreground py-2">
          No data available for analysis.
        </p>
      )}
    </div>
  )
}

// ============================================================================
// Analyze Status Strip
// ============================================================================

function AnalyzeStatusStrip({ status }: { status: NonNullable<AnalyzeResult['deep_status']> }) {
  const entries = Object.entries(status.section_status ?? {})
  if (entries.length === 0) {
    return null
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {entries.map(([name, value]) => (
        <span
          key={name}
          title={status.section_errors?.[name]}
          className={cn(
            'inline-flex h-5 items-center gap-1 rounded border px-1.5 text-[10px] capitalize',
            value === 'complete' && 'border-green-500/30 bg-green-500/10 text-green-600',
            value === 'running' && 'border-blue-500/30 bg-blue-500/10 text-blue-600',
            value === 'error' && 'border-red-500/30 bg-red-500/10 text-red-600',
            value === 'pending' && 'border-muted bg-muted/20 text-muted-foreground'
          )}
        >
          {value === 'running' && <Loader2 className="h-2.5 w-2.5 animate-spin" />}
          {formatAnalyzeSectionName(name)}
        </span>
      ))}
    </div>
  )
}

// ============================================================================
// Analyze Card
// ============================================================================

function AnalyzeCard({
  title,
  icon,
  children,
}: {
  title: string
  icon: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-md border bg-background/50 p-2.5 space-y-1.5">
      <div className="text-xs font-semibold flex items-center gap-1.5">
        <span>{icon}</span>
        {title}
      </div>
      {children}
    </div>
  )
}

// ============================================================================
// Shared sub-components
// ============================================================================

interface MetricTileProps {
  label: string
  value: string
  valueClassName?: string
}

function MetricTile({ label, value, valueClassName }: MetricTileProps) {
  return (
    <div className="rounded-lg border bg-muted/20 p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={cn('mt-1 text-lg font-semibold', valueClassName)}>{value}</p>
    </div>
  )
}

interface ExchangeDetailProps {
  exchangeData: {
    exchange: string
    spot?: MarketRecord
    futures?: MarketRecord
  }
}

function ExchangeDetail({ exchangeData }: ExchangeDetailProps) {
  const [expanded, setExpanded] = useState(false)

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded}>
      <div className="rounded-md border bg-muted/20 p-3">
        <div className="flex items-center justify-between">
          <ExchangeBadge exchange={exchangeData.exchange} />
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm">
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </Button>
          </CollapsibleTrigger>
        </div>

        <CollapsibleContent className="space-y-3 pt-3">
          {exchangeData.spot && (
            <div className="space-y-1 text-xs">
              <div className="font-semibold text-blue-500">SPOT</div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Price:</span>
                <span>{formatUsd(exchangeData.spot.price)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Volume (24h):</span>
                <span>{formatVolumeUsd(exchangeData.spot.volume_current)}</span>
              </div>
            </div>
          )}

          {exchangeData.futures && (
            <div className="space-y-1 text-xs">
              <div className="font-semibold text-orange-500">FUTURES</div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Price:</span>
                <span>{formatUsd(exchangeData.futures.price)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Volume (24h):</span>
                <span>{formatVolumeUsd(exchangeData.futures.volume_current)}</span>
              </div>
              {exchangeData.futures.open_interest && (
                <>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">OI:</span>
                    <span>{formatCompactNumber(exchangeData.futures.open_interest.current || 0)}</span>
                  </div>
                  {exchangeData.futures.change_current && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Change:</span>
                      <span
                        className={cn(
                          'font-semibold',
                          exchangeData.futures.change_current.startsWith('-')
                            ? 'text-red-500'
                            : 'text-green-500'
                        )}
                      >
                        {exchangeData.futures.change_current}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}

// ============================================================================
// Open Position Banner — shows live position status for a coin
// ============================================================================

function OpenPositionBanner({ symbol }: { symbol: string }) {
  const { data } = useLivePositions()
  const positions = data?.positions || []
  const pos = positions.find(
    (p) => p.symbol.toUpperCase() === symbol.toUpperCase()
  )

  if (!pos) return null

  const direction = (pos.direction || '').toUpperCase()
  const isLong = direction === 'LONG'
  const isShort = direction === 'SHORT'
  const directionLabel = direction || 'N/A'
  const pnl = pos.pnl_pct

  const formatPrice = (price: number | null) => {
    if (!price) return '—'
    return price < 1
      ? `$${price.toFixed(4)}`
      : `$${price.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
  }

  return (
    <div
      className={cn(
        'rounded-lg border p-3 flex items-center gap-3 text-sm',
        isLong
          ? 'bg-green-500/5 border-green-500/20'
          : isShort
            ? 'bg-red-500/5 border-red-500/20'
            : 'bg-muted/20 border-border'
      )}
    >
      <div
        className={cn(
          'flex h-7 w-7 items-center justify-center rounded-md text-xs font-bold',
          isLong
            ? 'bg-green-500/15 text-green-500'
            : isShort
              ? 'bg-red-500/15 text-red-500'
              : 'bg-muted text-muted-foreground'
        )}
      >
        {isLong ? (
          <ArrowUpRight className="h-4 w-4" />
        ) : isShort ? (
          <ArrowDownRight className="h-4 w-4" />
        ) : (
          <ArrowRight className="h-4 w-4" />
        )}
      </div>
      <div className="flex-1 flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="font-semibold">
          Open {directionLabel} Position
        </span>
        {pos.current_price && (
          <span className="text-muted-foreground">
            {formatPrice(pos.current_price)}
          </span>
        )}
        {pnl != null && (
          <Badge
            className={cn(
              'text-xs tabular-nums',
              pnl >= 0
                ? 'bg-green-500/15 text-green-500'
                : 'bg-red-500/15 text-red-500'
            )}
          >
            {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)}%
          </Badge>
        )}
        {pos.duration && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            {pos.duration}
          </span>
        )}
        {pos.users_in_position?.length > 0 && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Users className="h-3 w-3" />
            {pos.users_in_position.join(', ')}
          </span>
        )}
      </div>
    </div>
  )
}

function formatCompactNumber(num: number): string {
  if (num >= 1_000_000_000) return `${(num / 1_000_000_000).toFixed(2)}B`
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(2)}M`
  if (num >= 1_000) return `${(num / 1_000).toFixed(2)}K`
  return num.toFixed(2)
}

function formatVolumeUsd(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value) || value < 0) return 'N/A'
  return `$${formatCompactNumber(value)}`
}

function formatSignedPercent(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return 'N/A'
  const sign = value > 0 ? '+' : ''
  return `${sign}${value.toFixed(2)}%`
}

function formatUsd(value: number | null | undefined): string {
  if (value === null || value === undefined) return 'N/A'
  return `$${value.toLocaleString(undefined, { maximumFractionDigits: 4 })}`
}

function formatAnalyzeSectionName(value: string): string {
  return value.replace(/_/g, ' ')
}
